// Componente simplificado baseado em service_order/View.js
// Adaptado para visualização de orçamentos de serviço
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
    useTheme,
    Button,
    Drawer
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { httpClient } from "../../common/httpclient";
import { usePermissions } from "../../hooks/usePermissions";
import { useIsMobileShell } from "../../hooks/useIsMobileShell";
import { useEnterpriseConfigContext } from "../../component/context/EnterpriseConfigContext";
import QuoteWhatsappDialog, { useWhatsappConnected } from "../../component/dialog/QuoteWhatsappDialog";
import MobileQuoteDetail from "../../component/servicequote/MobileQuoteDetail";
import { quoteStatusKey } from "../../component/servicequote/quoteMobileUtils";
import PageScaffold from "../../component/pageScaffold/PageScaffold";
import QuoteData from "./quoteData";
import { formatCurrency } from "../../common/formatters";
import DialogForm from "../../component/dialog/DialogForm";

export default function View() {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobileShell = useIsMobileShell();
    const { hasPermission } = usePermissions();
    const canUpdateQuote = hasPermission('servicequote.update');
    const canDeleteQuote = hasPermission('servicequote.delete');
    const canSendWhatsapp = hasPermission('servicequote.send');
    const whatsappConnected = useWhatsappConnected(canSendWhatsapp);
    const { settings } = useEnterpriseConfigContext();
    const [quoteDetails, setQuoteDetails] = useState({});
    const [openWhatsappModal, setOpenWhatsappModal] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [downloadingPDF, setDownloadingPDF] = useState(false);
    const { id } = useParams();
    const [message, setMessage] = useState('');
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState('success');
    const [edit, setEdit] = useState(false);
    const [isForm, setIsForm] = useState(false);
    const quoteDataRef = useRef();
    const [quoteId, setQuoteId] = useState('');
    const [quoteNumber, setQuoteNumber] = useState('');
    const [wasUpdated, setWasUpdated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingItems, setLoadingItems] = useState(true);
    const [loadingValues, setLoadingValues] = useState(true);
    const [openConvertDialog, setOpenConvertDialog] = useState(false);

    const [quoteState, setQuoteState] = useState({
        id: '',
        total: '0,00',
        totalItems: 0,
        total_services: '0,00',
        total_products: '0,00',
        cart: [],
        status: 'Pendente',
        number: 0
    });

    const handleEdit = () => {
        setIsForm(true);
        setEdit(true);
        setQuoteId(id);
    };

    const closeQuoteDataSheet = () => {
        setIsForm(false);
        setEdit(false);
        setQuoteId('');
    };

    const handleSave = () => {
        if (quoteDataRef && quoteDataRef.current) {
            quoteDataRef.current.submitForm();
        }
    };

    const handleSubmit = (values) => {
        httpClient.put(`/servicequotes/${id}`, values).then(response => {
            if (response.status === 200 || response.status === 204) {
                setMessage('Orçamento salvo com sucesso');
                setSeverity('success');
                setOpenSnackbar(true);
                closeQuoteDataSheet();
                setWasUpdated(true);
            }
        }).catch(error => {
            setMessage('Erro ao salvar orçamento');
            setSeverity('error');
            setOpenSnackbar(true);
        });
    };

    const handleConvertToOS = () => {
        setOpenConvertDialog(true);
    };

    const confirmConvertToOS = async () => {
        try {
            const response = await httpClient.post(`/servicequotes/${id}/convert`);
            if (response.status === 200 || response.status === 201) {
                setMessage('Ordem de Serviço gerada com sucesso');
                setSeverity('success');
                setOpenSnackbar(true);
                setOpenConvertDialog(false);
                // Redirecionar para a OS criada se o ID estiver na resposta
                if (response.data?.id) {
                    navigate(`/serviceorders/${response.data.id}/view`);
                }
            }
        } catch (error) {
            console.error('Erro ao converter orçamento:', error);
            setMessage('Erro ao gerar ordem a partir do orçamento: ' + (error.response?.data?.message || error.message));
            setSeverity('error');
            setOpenSnackbar(true);
        }
    };

    const handleStatusChange = async (newStatus) => {
        try {
            const response = await httpClient.post(`/servicequotes/${id}/status`, { status: newStatus });
            if (response.status === 200) {
                setMessage('Status atualizado com sucesso');
                setSeverity('success');
                setOpenSnackbar(true);
                setQuoteState(prev => ({ ...prev, status: newStatus }));
            }
        } catch (error) {
            setMessage('Erro ao atualizar status');
            setSeverity('error');
            setOpenSnackbar(true);
        }
    };

    // Mobile (`MobileQuoteDetail`): mesmas chamadas da lista/operação do orçamento.
    const handleCopyPortalLink = async () => {
        try {
            const response = await httpClient.get(`/servicequotes/${id}/share-token`);
            const token = response.data?.token;
            if (response.status !== 200 || !token) {
                throw new Error('Token indisponível');
            }
            const link = `${window.location.origin}/orcamento/${id}?t=${encodeURIComponent(token)}`;
            await navigator.clipboard.writeText(link);
            setMessage('Link do orçamento copiado');
            setSeverity('success');
        } catch (error) {
            console.error('Erro ao copiar link do orçamento:', error);
            setMessage('Erro ao gerar o link do orçamento');
            setSeverity('error');
        }
        setOpenSnackbar(true);
    };

    const handleDownloadPDF = async () => {
        try {
            setDownloadingPDF(true);
            const response = await httpClient.get(`/servicequotes/${id}/pdf`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const urlBlob = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = urlBlob;
            link.download = `orcamento_servico_${quoteNumber || id}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(urlBlob);
            setMessage('PDF baixado com sucesso');
            setSeverity('success');
            setOpenSnackbar(true);
        } catch (error) {
            console.error('Erro ao baixar PDF:', error);
            setMessage('Erro ao baixar PDF do orçamento');
            setSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setDownloadingPDF(false);
        }
    };

    const confirmDeleteQuote = async () => {
        try {
            const response = await httpClient.delete(`/servicequotes/${id}`);
            if (response.status === 200 || response.status === 204) {
                setMessage('Orçamento removido com sucesso');
                setSeverity('success');
                setOpenSnackbar(true);
                setOpenDeleteDialog(false);
                setTimeout(() => navigate('/servicequotes'), 500);
            }
        } catch (error) {
            console.error('Erro ao remover orçamento:', error);
            setMessage('Erro ao remover orçamento: ' + (error.response?.data?.message || error.message));
            setSeverity('error');
            setOpenSnackbar(true);
            setOpenDeleteDialog(false);
        }
    };

    const fetchItems = async () => {
        try {
            setLoadingItems(true);
            const response = await httpClient.get(`/servicequotes/${id}/items`);
            const items = response.data || [];
            setQuoteState(prev => ({ ...prev, cart: items }));
        } catch (error) {
            console.error('Erro ao carregar itens:', error);
        } finally {
            setLoadingItems(false);
        }
    };

    const fetchValues = async () => {
        try {
            setLoadingValues(true);
            const response = await httpClient.get(`/servicequotes/${id}/values`);
            
            setQuoteState(prev => ({
                ...prev,
                total: response.data.amount || '0,00',
                totalItems: response.data.count_items || 0,
                total_products: response.data.amount_products || '0,00',
                total_services: response.data.amount_services || '0,00'
            }));
        } catch (error) {
            console.error('Erro ao carregar valores:', error);
        } finally {
            setLoadingValues(false);
        }
    };

    const fetchQuoteBasicData = async () => {
        try {
            const response = await httpClient.get(`/servicequotes/${id}`);
            if (response.status === 200) {
                setQuoteState(prev => ({
                    ...prev,
                    id: response.data.id,
                    number: response.data.number,
                    status: response.data.status || 'Pendente'
                }));
                setQuoteNumber(response.data.number);
                setQuoteDetails(response.data || {});
            }
        } catch (error) {
            console.error('Erro ao carregar dados básicos do orçamento:', error);
        }
    };

    useEffect(() => {
        if (id) {
            setQuoteId(id);
            setLoading(true);

            const promises = [
                fetchQuoteBasicData(),
                fetchItems(),
                fetchValues()
            ];
            
            Promise.all(promises).finally(() => {
                setLoading(false);
            });
        }
    }, [id, wasUpdated]);

    const breadcrumbs = [
        { title: 'Orçamentos de Serviço', link: '/servicequotes' },
    ];

    const getStatusColor = (status) => {
        switch (status) {
            case 'Aprovado':
                return 'success';
            case 'Pendente':
                return 'warning';
            case 'Rejeitado':
                return 'error';
            case 'Convertido':
                return 'info';
            default:
                return 'default';
        }
    };

    const canConvertQuote = canUpdateQuote && (quoteState.status === 'Pendente' || quoteState.status === 'Aprovado');
    // Mobile: converter só quando aprovado (mesma regra da operação do orçamento).
    const statusKey = quoteStatusKey(quoteState.status);
    const canConvertMobile = canUpdateQuote && statusKey === 'aprovado';

    // Gaveta de edição (dados do orçamento) — a mesma no desktop e no mobile.
    const editDrawer = (
        <Drawer
            anchor="right"
            open={isForm}
            onClose={closeQuoteDataSheet}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(0,0,0,0.4)',
                        backdropFilter: 'blur(4px)'
                    }
                }
            }}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 560, md: 640 },
                    maxWidth: '100vw',
                    top: { xs: '56px', sm: '64px' },
                    height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' },
                    borderTopLeftRadius: 16,
                    borderBottomLeftRadius: 0,
                    boxShadow: theme.palette.mode === 'dark'
                        ? '-8px 0 32px rgba(0,0,0,0.4)'
                        : '-8px 0 32px rgba(0,0,0,0.12)',
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }
            }}
        >
            <Box
                sx={{
                    flexShrink: 0,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: theme.palette.background.paper,
                    pt: 3
                }}
            >
                <Box sx={{ pt: 1.5, pb: 0.5, display: 'flex', justifyContent: 'center' }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 4,
                            borderRadius: 2,
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.2)'
                                : 'rgba(0,0,0,0.12)'
                        }}
                    />
                </Box>
                <Box
                    sx={{
                        px: 3,
                        pt: 5,
                        pb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Dados do Orçamento
                    </Typography>
                    <IconButton onClick={closeQuoteDataSheet} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                {isForm && (
                    <QuoteData
                        ref={quoteDataRef}
                        isForm={true}
                        id={quoteId || id}
                        setQuoteNumber={setQuoteNumber}
                        wasUpdated={wasUpdated}
                        handleSubmit={handleSubmit}
                        editMode={edit}
                    />
                )}
            </Box>

            <Box sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider'
            }}>
                <Button variant="outlined" onClick={closeQuoteDataSheet}>
                    Cancelar
                </Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>
                    Salvar
                </Button>
            </Box>
        </Drawer>
    );

    if (isMobileShell) {
        return (
            <PageScaffold
                title="Orçamento de Serviço"
                links={breadcrumbs}
                openSnackbar={openSnackbar}
                message={message}
                setOpenSnackbar={setOpenSnackbar}
                severity={severity}
            >
                <MobileQuoteDetail
                    loading={loading}
                    quoteNumber={quoteNumber}
                    status={quoteState.status}
                    details={quoteDetails}
                    usesVehicle={Boolean(settings?.os_use_vehicle)}
                    items={quoteState.cart}
                    loadingItems={loadingItems}
                    total={quoteState.total}
                    canEdit={canUpdateQuote}
                    canDelete={canDeleteQuote}
                    canConvert={canConvertMobile}
                    canSendWhatsapp={canSendWhatsapp}
                    whatsappConnected={whatsappConnected}
                    converted={statusKey === 'ordem gerada'}
                    printing={downloadingPDF}
                    onPrint={handleDownloadPDF}
                    onEdit={handleEdit}
                    onDelete={() => setOpenDeleteDialog(true)}
                    onConvert={handleConvertToOS}
                    onOperate={() => navigate(`/servicequotes/${id}/operator`)}
                    onCopyLink={handleCopyPortalLink}
                    onWhatsapp={() => setOpenWhatsappModal(true)}
                />
                <QuoteWhatsappDialog
                    open={openWhatsappModal}
                    quoteId={id}
                    quoteNumber={quoteNumber}
                    onClose={() => setOpenWhatsappModal(false)}
                    onSent={() => setWasUpdated((v) => !v)}
                    onResult={(msg, sev) => {
                        setMessage(msg);
                        setSeverity(sev);
                        setOpenSnackbar(true);
                    }}
                />
                <DialogForm
                    iconType={'warning'}
                    open={openDeleteDialog}
                    title={'Excluir Orçamento'}
                    confirmButtonText={'Confirmar Exclusão'}
                    content={'Tem certeza que deseja excluir este orçamento? Esta ação não pode ser desfeita.'}
                    onSubmit={confirmDeleteQuote}
                    onClose={() => setOpenDeleteDialog(false)}
                />
                <DialogForm
                    iconType={'info'}
                    open={openConvertDialog}
                    title={'Gerar Ordem de Serviço'}
                    confirmButtonText={'Sim, Gerar'}
                    content={'Tem certeza que deseja gerar uma Ordem de Serviço a partir deste orçamento?'}
                    secondaryContent={'Esta ação criará uma nova Ordem de Serviço baseada neste orçamento.'}
                    onSubmit={confirmConvertToOS}
                    onClose={() => setOpenConvertDialog(false)}
                />
                {editDrawer}
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            title="Orçamento de Serviço"
            links={breadcrumbs}
            openSnackbar={openSnackbar}
            message={message}
            setOpenSnackbar={setOpenSnackbar}
            severity={severity}
        >
            <Box sx={{ width: '100%', minHeight: '100vh', p: 3 }}>
                {loading ? (
                    <Box sx={{ textAlign: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Card sx={{
                            borderRadius: 3,
                            mb: 3,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                            border: '1px solid',
                            borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'divider',
                            bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : 'background.paper'
                        }}>
                            <Box sx={{
                                background: theme.palette.mode === 'dark' 
                                    ? 'linear-gradient(135deg, #1a1a1a 0%, #000000 100%)'
                                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                p: 3,
                                borderRadius: '12px 12px 0 0',
                            }}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 2,
                                    justifyContent: 'space-between'
                                }}>
                                    <Box>
                                        <Typography variant="h4" sx={{ 
                                            color: 'white', 
                                            fontWeight: 700, 
                                            mb: 0.5 
                                        }}>
                                            ORÇAMENTO #{quoteNumber || id}
                                        </Typography>
                                        <Chip
                                            label={quoteState.status || 'Pendente'}
                                            color={getStatusColor(quoteState.status)}
                                            sx={{
                                                bgcolor: 'rgba(255,255,255,0.2)',
                                                color: 'white',
                                                fontWeight: 600
                                            }}
                                        />
                                    </Box>
                                    <Stack direction="row" spacing={1}>
                                        {!isMobileShell && <Tooltip title="Voltar">
                                            <IconButton
                                                onClick={() => navigate('/servicequotes')}
                                                sx={{
                                                    bgcolor: 'rgba(255,255,255,0.2)',
                                                    color: 'white',
                                                    '&:hover': { 
                                                        bgcolor: 'rgba(255,255,255,0.3)'
                                                    }
                                                }}
                                            >
                                                <ArrowBackIcon />
                                            </IconButton>
                                        </Tooltip>}
                                        {!isMobileShell && canUpdateQuote && (
                                        <Tooltip title="Editar">
                                            <IconButton
                                                onClick={handleEdit}
                                                sx={{
                                                    bgcolor: 'rgba(255,255,255,0.2)',
                                                    color: 'white',
                                                    '&:hover': { 
                                                        bgcolor: 'rgba(255,255,255,0.3)'
                                                    }
                                                }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        )}
                                        {!isMobileShell && canConvertQuote ? (
                                            <Tooltip title="Gerar Ordem">
                                                <IconButton
                                                    onClick={handleConvertToOS}
                                                    sx={{
                                                        bgcolor: 'rgba(255,255,255,0.2)',
                                                        color: 'white',
                                                        '&:hover': { 
                                                            bgcolor: 'rgba(255,255,255,0.3)'
                                                        }
                                                    }}
                                                >
                                                    <SwapHorizIcon />
                                                </IconButton>
                                            </Tooltip>
                                        ) : null}
                                    </Stack>
                                </Box>
                            </Box>

                            <CardContent>
                                <QuoteData
                                    ref={quoteDataRef}
                                    isForm={false}
                                    id={id}
                                    setQuoteNumber={setQuoteNumber}
                                    wasUpdated={wasUpdated}
                                    handleSubmit={handleSubmit}
                                    editMode={edit}
                                />
                            </CardContent>
                        </Card>

                        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
                            <Paper sx={{ p: 3, borderRadius: 3, flex: 1 }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                    Itens do Orçamento
                                </Typography>
                                {loadingItems ? (
                                    <Box sx={{ textAlign: 'center', p: 4 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : quoteState.cart.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 4 }}>
                                        Nenhum item adicionado ao orçamento
                                    </Typography>
                                ) : (
                                    <Stack spacing={2}>
                                        {quoteState.cart.map((item) => (
                                            <Box
                                                key={item.id}
                                                sx={{
                                                    p: 2,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderRadius: 1,
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <Box>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {item.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {item.type === 'product' ? 'Produto' : 'Serviço'} • Qtd: {item.quantity}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {formatCurrency(String(item.amount_total || item.total || 0))}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                            </Paper>

                            <Paper sx={{ p: 3, borderRadius: 3, minWidth: { xs: 0, sm: '300px' } }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                    Resumo Financeiro
                                </Typography>
                                <Stack spacing={2}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Total de Itens:
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600}>
                                            {quoteState.totalItems}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Produtos:
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600}>
                                            {formatCurrency(String(quoteState.total_products || '0'))}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Serviços:
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600}>
                                            {formatCurrency(String(quoteState.total_services || '0'))}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between',
                                        pt: 2,
                                        borderTop: '1px solid',
                                        borderColor: 'divider'
                                    }}>
                                        <Typography variant="h6" fontWeight={600}>
                                            Total:
                                        </Typography>
                                        <Typography variant="h6" fontWeight={600} color="primary">
                                            {formatCurrency(String(quoteState.total || '0'))}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Box>
                    </>
                )}
            </Box>

            <DialogForm
                iconType={'info'}
                open={openConvertDialog}
                title={'Gerar Ordem de Serviço'}
                confirmButtonText={'Sim, Gerar'}
                content={'Tem certeza que deseja gerar uma Ordem de Serviço a partir deste orçamento?'}
                secondaryContent={'Esta ação criará uma nova Ordem de Serviço baseada neste orçamento.'}
                onSubmit={confirmConvertToOS}
                onClose={() => {
                    setOpenConvertDialog(false);
                }}
            />
            {editDrawer}
        </PageScaffold>
    );
}

